import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SITE } from "@/constants/site";

export const ADMIN_LOGIN_IDS = {
  root: "admin-login",
  email: "admin-login-email",
  password: "admin-login-password",
  submit: "admin-login-submit",
  error: "admin-login-error",
};

const AdminLogin = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "TWGAS · Admin Login";
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") {
      const from = location.state?.from || "/admin";
      navigate(from, { replace: true });
    }
  }, [user, navigate, location.state]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Logged in.");
  };

  const input =
    "w-full bg-white/[0.03] border border-white/10 px-4 py-4 text-white placeholder:text-zinc-600 font-mono-plex text-sm tracking-wide focus:outline-none focus:border-[#FF4D00] focus:bg-white/[0.06]";

  return (
    <main
      data-testid={ADMIN_LOGIN_IDS.root}
      className="min-h-screen bg-[#0A0A0A] pt-32 md:pt-40 pb-24"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="num-tag mb-4">/ ADMIN · SECURE AREA</div>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display uppercase text-6xl md:text-7xl leading-[0.86] tracking-tight"
          >
            Sign in.
            <br />
            <span className="stroke-fire">Own the work.</span>
          </motion.h1>
          <p className="mt-6 text-zinc-400 max-w-[42ch] text-base md:text-lg leading-relaxed">
            Restricted area for TWGAS staff. Access to lead submissions and
            program controls.
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 font-mono-plex text-[11px] tracking-[0.28em] uppercase text-zinc-500 hover:text-[#FF4D00] transition-colors"
          >
            ← Back to site
          </Link>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          <form
            onSubmit={onSubmit}
            className="border border-white/10 bg-[#0F0F0F] p-6 md:p-10 space-y-4"
            noValidate
          >
            <div className="flex items-center justify-between mb-2">
              <div className="num-tag flex items-center gap-2">
                <Lock size={12} className="text-[#FF4D00]" />
                / CREDENTIALS
              </div>
              <div className="font-mono-plex text-[10px] tracking-[0.28em] text-zinc-500 uppercase">
                Encrypted
              </div>
            </div>

            <label className="block">
              <span className="block font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-2">
                / Email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={ADMIN_LOGIN_IDS.email}
                placeholder={SITE.ownerEmail}
                className={input}
                autoComplete="email"
                required
              />
            </label>

            <label className="block">
              <span className="block font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-2">
                / Password
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={ADMIN_LOGIN_IDS.password}
                placeholder="••••••••"
                className={input}
                autoComplete="current-password"
                required
              />
            </label>

            {error && (
              <div
                data-testid={ADMIN_LOGIN_IDS.error}
                className="border border-red-500/30 bg-red-500/10 text-red-300 px-4 py-3 text-sm"
              >
                {error}
              </div>
            )}

            <div className="pt-2 flex items-center justify-between gap-4">
              <p className="text-xs text-zinc-500">
                Session lasts 24 hours.
              </p>
              <button
                type="submit"
                data-testid={ADMIN_LOGIN_IDS.submit}
                disabled={submitting}
                className="btn-fire disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Signing in…" : "Sign In"}
                <ArrowUpRight size={18} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
};

export default AdminLogin;
